import { useState, useEffect } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { Button } from '@app/components/Button/Button';
import {
  getUsersAdmin,
  updateUserAdmin,
  deleteUserAdmin,
} from '@app/services/user';
import { impersonateUser } from '@app/services/admin';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { GenericTable } from '@app/components/GenericTable/GenericTable';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react';
import { UserInterface, UserRole, UserStatus } from '@app/models';
import { getRoleLabel, isUserAdmin, isUserSuperAdmin } from '@app/utils/user';
import { useAppSelector } from '@app/redux/hooks';
import { toast } from 'react-toastify';
import { useForm, FieldValues, FormProvider } from 'react-hook-form';
import { InputField } from '@app/components/Fields/InputField';
import { Select } from '@app/components/Select/Select';
import { PhoneField } from '@app/components';
import { ConfirmDialog } from '@app/components/ConfirmDialog/ConfirmDialog';
import { adminUserUpdateValidation } from '@app/utils/validations';
import { useDebounce } from '@app/utils/use-debounce';
import SearchIcon from '@app/assets/icons/search.svg?react';
import NoCustomerImage from '@app/assets/icons/no-customers.svg?react';

const StatusBadge = ({ status }: { status: UserStatus }) => {
  const statusStyles = {
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-gray-100 text-gray-800',
    SUSPENDED: 'bg-red-100 text-red-800',
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        statusStyles[status] || 'bg-gray-100 text-gray-800'
      }`}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
};

const HEADER_COLUMNS = [
  { label: 'User', field: 'firstName', sortable: false },
  { label: 'Email', field: 'email', sortable: false },
  { label: 'Role', field: 'role', sortable: false },
  { label: 'Created', field: 'createdAt', sortable: false },
  { label: 'Last Active', field: 'lastActiveAt', sortable: false },
  { label: 'Status', field: 'status', sortable: false },
  { label: 'Actions', field: 'actions', sortable: false },
];

export const AdminUsers = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<UserInterface | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserInterface | null>(null);
  const queryClient = useQueryClient();
  const currentUser = useAppSelector((state) => state.auth.user);
  const [perPage, setPerPage] = useState(10);
  const debouncedSearch = useDebounce(searchTerm, 500);

  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['users', 'admin', currentPage, debouncedSearch, perPage],
    queryFn: () =>
      getUsersAdmin(
        currentPage,
        perPage,
        debouncedSearch.length > 2 ? debouncedSearch : undefined
      ),
    placeholderData: keepPreviousData,
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: any }) =>
      updateUserAdmin(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      setEditingUser(null);
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => deleteUserAdmin(userId),
    onSuccess: () => {
      toast.success('User has been deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['logs'] });
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    },
    onError: () => {
      toast.error('Failed to delete user. Please try again.');
    },
  });

  // Reset to first page when search term changes
  useEffect(() => {
    if (debouncedSearch.length > 2) {
      setCurrentPage(1);
    }
  }, [debouncedSearch]);

  const handleUpdateUser = async (values: FieldValues) => {
    if (!editingUser?.id) return;

    const userData: any = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      role: values.role,
    };

    if (values.isAdmin !== undefined) {
      userData.isAdmin = values.isAdmin;
    }

    if (values.password) {
      userData.password = values.password;
    }

    if (values.resetPassword) {
      userData.resetPassword = values.resetPassword;
    }

    try {
      await updateUserMutation.mutateAsync({
        userId: editingUser.id,
        userData,
      });
      toast.success('User has been updated successfully!');
    } catch {
      toast.error('Failed to update user. Please try again.');
    }
  };

  const handleDeleteUser = (user: UserInterface) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = () => {
    if (userToDelete?.id) {
      deleteUserMutation.mutate(userToDelete.id);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  const handleSendPasswordReset = async (userId: string) => {
    try {
      await updateUserMutation.mutateAsync({
        userId,
        userData: {
          resetPassword: true,
        },
      });
      toast.success('Password reset email sent successfully!');
    } catch {
      toast.error('Error sending password reset email. Please try again.');
    }
  };

  const handleToggleUserStatus = async (
    userId: string,
    currentStatus: UserStatus
  ) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

    try {
      await updateUserMutation.mutateAsync({
        userId,
        userData: { status: newStatus },
      });
      toast.success('User status updated successfully!');
    } catch {
      toast.error('Failed to update user status. Please try again.');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEditUser = (user: UserInterface) => {
    setEditingUser(user);
  };

  const handleCloseEditModal = () => {
    setEditingUser(null);
  };

  const handleImpersonateUser = async (userId: string) => {
    try {
      const currentToken = localStorage.getItem('accessToken');
      if (currentToken) {
        localStorage.setItem('adminToken', currentToken);
      }

      const response = await impersonateUser(userId);

      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem(
        'impersonation',
        JSON.stringify(response.impersonation)
      );

      queryClient.invalidateQueries({ queryKey: ['logs'] });

      if (response.businesses?.length > 0) {
        localStorage.setItem('businessId', response.businesses[0].id);
      }

      window.location.href = '/';
    } catch {
      toast.error('Failed to impersonate user. Please try again.');
    }
  };

  const onSubmitEdit = (values: FieldValues) => {
    handleUpdateUser(values);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">
          Error loading users. Please try again.
        </div>
      </div>
    );
  }

  const renderRows = (users: UserInterface[]) => {
    return users.map((user) => (
      <tr key={user.id}>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-10 w-10">
              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-700">
                  {user.firstName?.charAt(0) || ''}
                  {user.lastName?.charAt(0) || ''}
                </span>
              </div>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900">
                {user.firstName} {user.lastName}
              </div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {user.email}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          <span
            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              user && isUserAdmin(user.role)
                ? 'bg-blue-100 text-blue-800'
                : 'bg-green-100 text-green-800'
            }`}
          >
            {getRoleLabel(user.role)}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {format(new Date(user.createdAt), 'dd-MM-yyyy')}
        </td>
        <td
          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
          title={
            user.lastActiveAt
              ? format(new Date(user.lastActiveAt), 'PPpp')
              : 'Never been active'
          }
        >
          {user.lastActiveAt
            ? formatDistanceToNow(new Date(user.lastActiveAt), {
                addSuffix: true,
              })
            : 'Never'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <StatusBadge status={user.status} />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          <Menu>
            <MenuButton disabled={user && isUserSuperAdmin(user.role)}>
              <Button
                kind="secondary"
                size="small"
                className="text-gray-600 hover:text-gray-900"
                disabled={user && isUserSuperAdmin(user.role)}
              >
                Actions
              </Button>
            </MenuButton>
            <MenuItems
              transition
              anchor="bottom end"
              className="w-48 p-1 bg-white shadow-lg rounded-md border border-gray-200 transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 z-10"
            >
              <MenuItem>
                <button
                  onClick={() => handleEditUser(user)}
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Edit
                </button>
              </MenuItem>
              {!isUserAdmin(user.role) && user.id !== currentUser?.id && (
                <MenuItem>
                  <button
                    onClick={() => handleImpersonateUser(user.id)}
                    className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    View as user
                  </button>
                </MenuItem>
              )}
              <MenuItem>
                <button
                  onClick={() => handleToggleUserStatus(user.id, user.status)}
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  {user.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() => handleSendPasswordReset(user.id)}
                  className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md text-left"
                >
                  Send Password Reset Email
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() => handleDeleteUser(user)}
                  className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md"
                >
                  Delete
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </td>
      </tr>
    ));
  };

  const users = usersData?.data || [];
  const total = usersData?.total || 0;
  const hasActiveFilters = Boolean(debouncedSearch);
  const hasData = Boolean(users.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Users Management</h2>
      </div>

      {/* Search Bar */}
      <div className="bg-white w-full p-4 flex flex-row gap-4 flex-wrap">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-gray-100 border-0 rounded-lg py-3 px-4 outline-brand-baseBlue outline-1 text-gray-600 placeholder:text-gray-500 focus:bg-gray-200 focus:outline-none w-[300px]"
          />
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}
            className="flex items-center gap-1 text-red-500 hover:text-red-600 transition-colors cursor-pointer"
          >
            <span className="text-sm">Reset</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Users Table */}
      <div className="overflow-auto bg-white">
        <div className="px-4 py-6">
          <div className="mb-4">
            <h3 className="text-2xl font-bold">Users ({total} total)</h3>
          </div>

          {!hasData && !isLoading && hasActiveFilters && (
            <>
              <br />
              <br />
              <br />
              <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
                <div className="rounded-lg bg-white p-4">
                  <SearchIcon />
                </div>
                <p className="font-bold">No users</p>
                <p className="text-sm text-base-coolGray text-center">
                  No users match your current search.
                  <br /> Consider adjusting your search.
                </p>
              </div>
            </>
          )}
          {!hasData && !isLoading && !hasActiveFilters && (
            <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
              <div className="rounded-lg bg-white p-4">
                <NoCustomerImage />
              </div>
              <p className="font-bold">No users</p>
              <p className="text-sm text-base-coolGray text-center">
                There are no users to display.
                <br />
                Users will appear here when they are created or invited.
              </p>
            </div>
          )}
          {hasData && !isLoading && (
            <GenericTable<UserInterface>
              data={users}
              headers={HEADER_COLUMNS}
              renderRow={renderRows}
              currentPage={currentPage}
              isLoading={isLoading}
              pagination={{
                total,
                handleLimitChange: (nextPerPage) =>
                  setPerPage(nextPerPage || 10),
                handleNextPage: handlePageChange,
                handlePrevPage: handlePageChange,
              }}
            />
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <UserEditForm
          initialValues={editingUser}
          onSubmit={onSubmitEdit}
          onCancel={handleCloseEditModal}
          isLoading={updateUserMutation.isPending}
          isCurrentUserSuperAdmin={
            !!currentUser && isUserSuperAdmin(currentUser.role)
          }
        />
      )}

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={cancelDeleteUser}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete ${userToDelete?.firstName} ${userToDelete?.lastName}? This action cannot be undone.`}
        confirmText="Delete User"
        cancelText="Cancel"
        isLoading={deleteUserMutation.isPending}
        variant="danger"
      />
    </div>
  );
};

interface UserEditFormProps {
  onSubmit: (values: FieldValues) => void;
  onCancel: () => void;
  isLoading: boolean;
  isCurrentUserSuperAdmin: boolean;
  initialValues?: UserInterface;
}

const UserEditForm = ({
  onSubmit,
  onCancel,
  isLoading,
  isCurrentUserSuperAdmin,
  initialValues,
}: UserEditFormProps) => {
  const editForm = useForm({
    resolver: adminUserUpdateValidation,
    defaultValues: {
      firstName: initialValues?.firstName || '',
      lastName: initialValues?.lastName || '',
      email: initialValues?.email || '',
      phoneNumber: initialValues?.phoneNumber || '',
      role: initialValues?.role,
    },
  });

  const watchedPassword = editForm.watch('password');

  return (
    <Dialog open={true} onClose={onCancel} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="space-y-4 border bg-white w-full max-w-2xl rounded-xl max-h-[90vh] overflow-y-auto">
          <FormProvider {...editForm}>
            <div className="p-5">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit User
              </h3>
              <form
                onSubmit={editForm.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField name="firstName" label="First Name" type="text" />
                  <InputField name="lastName" label="Last Name" type="text" />
                </div>

                <InputField name="email" label="Email" type="email" />

                <PhoneField name="phoneNumber" label="Phone number" />

                <Select
                  onChange={(value) =>
                    editForm.setValue('role', value as UserRole)
                  }
                  value={editForm.watch('role') || ''}
                  name="role"
                  label="Role"
                  options={[
                    {
                      label: 'Admin',
                      value: 'ADMIN',
                    },
                    {
                      label: 'User',
                      value: 'USER',
                    },
                  ]}
                />

                {isCurrentUserSuperAdmin && (
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      Update Password
                    </h4>
                    <InputField
                      name="password"
                      label="New Password"
                      type="password"
                      placeholder="Enter new password (min 8 characters)"
                      isOptional
                    />
                    {watchedPassword && (
                      <InputField
                        name="confirmPassword"
                        label="Confirm Password"
                        type="password"
                        placeholder="Confirm new password"
                      />
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    onClick={onCancel}
                    kind="secondary"
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    kind="primary"
                    loading={isLoading}
                    disabled={isLoading}
                  >
                    Update User
                  </Button>
                </div>
              </form>
            </div>
          </FormProvider>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
